import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest, extractErrorMessage } from '@/lib/api-client';
import { toast } from '@/hooks/use-toast';
import {
  getCurrentTenantId,
  setTenantId,
  clearTenantId,
  canSwitchTenant,
} from '@/lib/tenant';

interface User {
  _id: string;
  name: string;
  surname: string;
  firstName?: string; // Tenant users use firstName
  lastName?: string; // Tenant users use lastName
  email: string;
  phone: string;
  status: string;
  userType?: string; // Regular users have userType
  role?: string; // Tenant admins have role instead of userType
  isAdmin?: boolean; // Tenant admin flag
  gym: string[];
  tenantId?: string; // Multi-tenancy support
  createdAt: string;
  updatedAt: string;
}

interface LoginCredentials {
  email: string;
  password: string;
  tenantId?: string; // Multi-tenancy support
}

interface RegisterData {
  email: string;
  password: string;
  phone: string;
  userType: string;
  name: string;
  surname: string;
  status: string;
  tenantId?: string; // Multi-tenancy support
}

interface UserContextType {
  user: User | null;
  isLoading: boolean;
  tenantId: string | null;
  login: (credentials: LoginCredentials) => Promise<User | null>;
  register: (userData: RegisterData) => Promise<User | null>;
  logout: () => void;
  fetchUserData: () => Promise<User | null>;
  switchTenant: (tenantId: string) => void;
  isAuthenticated: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

// Define allowed user types for the admin dashboard
const ALLOWED_USER_TYPES = ['admin', 'owner', 'trainer', 'reception'];

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tenantId, setCurrentTenantId] = useState<string | null>(null);
  const navigate = useNavigate();

  // Initialize tenantId on mount
  useEffect(() => {
    const currentTenant = getCurrentTenantId();
    if (currentTenant) {
      setCurrentTenantId(currentTenant);
    }
  }, []);

  // Function to switch tenant (only allowed for non-whitelabel apps)
  const switchTenant = (newTenantId: string) => {
    if (!canSwitchTenant()) {
      console.warn('Tenant switching is not allowed for whitelabel apps');
      return;
    }
    setTenantId(newTenantId);
    setCurrentTenantId(newTenantId);
  };

  const fetchUserData = async () => {
    // Skip authentication check on public routes
    const isPublicRoute =
      typeof window !== 'undefined' &&
      (window.location.pathname.startsWith('/register') ||
        window.location.pathname.startsWith('/login') ||
        window.location.pathname.startsWith('/privacy') ||
        window.location.pathname.startsWith('/support') ||
        window.location.pathname.startsWith('/forgot-password'));

    console.log(
      'fetchUserData: pathname=',
      typeof window !== 'undefined' ? window.location.pathname : 'SSR',
      'isPublicRoute=',
      isPublicRoute
    );

    if (isPublicRoute) {
      setIsLoading(false);
      return null;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');

      if (!token) {
        setUser(null);
        setIsLoading(false);
        return null;
      }

      const response = await apiRequest({
        method: 'GET',
        url: '/auth/me',
      });

      if (response && response.results) {
        // Check if user type is allowed to access the admin dashboard
        // Allow access if userType OR role is in the allowed list (tenant admins have role instead of userType)
        // Also allow if isAdmin is true (tenant admin flag)
        const userTypeAllowed = ALLOWED_USER_TYPES.includes(
          response.results.userType
        );
        const roleAllowed = ALLOWED_USER_TYPES.includes(response.results.role);
        const isAdminUser = response.results.isAdmin === true;

        if (!userTypeAllowed && !roleAllowed && !isAdminUser) {
          localStorage.removeItem('auth_token');
          setUser(null);
          // Only redirect if we're not already on a public route
          const currentPath =
            typeof window !== 'undefined' ? window.location.pathname : '';
          const isCurrentlyPublic =
            currentPath.startsWith('/register') ||
            currentPath.startsWith('/login') ||
            currentPath.startsWith('/privacy') ||
            currentPath.startsWith('/support') ||
            currentPath.startsWith('/forgot-password');

          if (!isCurrentlyPublic) {
            navigate('/login?error=accessDenied');
          }
          return null;
        }

        setUser(response.results);
        return response.results;
      }

      return null;
    } catch (error: any) {
      console.error('Failed to fetch user data:', error);
      
      // Only remove auth token on 401 (authentication) errors
      // Don't auto logout on 403 (forbidden) or 404 (not found)
      if (error?.response?.status === 401) {
        localStorage.removeItem('auth_token');
        setUser(null);
      }
      
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch user data if not on a public route
    const isPublicRoute =
      typeof window !== 'undefined' &&
      (window.location.pathname.startsWith('/register') ||
        window.location.pathname.startsWith('/login') ||
        window.location.pathname.startsWith('/privacy') ||
        window.location.pathname.startsWith('/support') ||
        window.location.pathname.startsWith('/forgot-password'));

    if (!isPublicRoute) {
      fetchUserData();
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (credentials: LoginCredentials) => {
    setIsLoading(true);
    try {
      const tenantId =
        credentials.tenantId || getCurrentTenantId() || undefined;
      const loginData = {
        ...credentials,
        tenantId: tenantId || undefined,
      };

      // Persist tenant before request so getTenantHeaders picks it up
      if (tenantId) {
        setTenantId(tenantId);
      }

      const response = await apiRequest({
        method: 'POST',
        url: '/auth/login',
        data: loginData,
        headers: tenantId ? { 'X-Tenant-ID': tenantId } : {},
      });

      const {
        token,
        tenantId: responseTenantId,
        ...userData
      } = response.results;

      // Debug: Log the user data to see what we're getting
      console.log('Login response userData:', userData);
      console.log(
        'userType:',
        userData.userType,
        'role:',
        userData.role,
        'isAdmin:',
        userData.isAdmin
      );

      // Check if user type is allowed to access the admin dashboard
      // Allow access if userType OR role is in the allowed list (tenant admins have role instead of userType)
      // Also allow if isAdmin is true (tenant admin flag)
      const userTypeAllowed = ALLOWED_USER_TYPES.includes(userData.userType);
      const roleAllowed = ALLOWED_USER_TYPES.includes(userData.role);
      const isAdminUser = userData.isAdmin === true;

      console.log(
        'Access check - userTypeAllowed:',
        userTypeAllowed,
        'roleAllowed:',
        roleAllowed,
        'isAdminUser:',
        isAdminUser
      );

      if (!userTypeAllowed && !roleAllowed && !isAdminUser) {
        toast({
          title: 'Access Denied',
          description:
            "Regular users don't have access to the admin system. Please use the member portal instead.",
          variant: 'destructive',
        });

        setIsLoading(false);
        return null;
      }

      // Store tenant ID from response
      if (responseTenantId) {
        setTenantId(responseTenantId);
        setCurrentTenantId(responseTenantId);
      }

      localStorage.setItem('auth_token', token);
      setUser(userData);

      toast({
        title: 'Success',
        description: 'Logged in successfully',
      });

      navigate('/dashboard');
      return userData;
    } catch (err) {
      // Extract the error message using our helper function
      let errorMessage = 'Failed to login';

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (err && typeof err === 'object' && 'message' in err) {
        // Try to extract from complex error object
        errorMessage = extractErrorMessage(err);
      }

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });

      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: RegisterData) => {
    setIsLoading(true);
    try {
      // Ensure we're not registering a regular user in the admin system
      if (userData.userType === 'user') {
        toast({
          title: 'Error',
          description: 'Cannot register regular users in the admin system',
          variant: 'destructive',
        });
        setIsLoading(false);
        throw new Error('Cannot register regular users in the admin system');
      }

      // Include tenantId in the register request if provided or available
      const registerData = {
        ...userData,
        tenantId: userData.tenantId || getCurrentTenantId() || undefined,
      };

      const response = await apiRequest({
        method: 'POST',
        url: '/auth/register',
        data: registerData,
      });

      const {
        token,
        tenantId: responseTenantId,
        ...userInfo
      } = response.results;

      localStorage.setItem('auth_token', token);
      setUser(userInfo);

      // Store tenant ID from response
      if (responseTenantId) {
        setTenantId(responseTenantId);
        setCurrentTenantId(responseTenantId);
      }

      toast({
        title: 'Success',
        description: 'Registered successfully',
      });

      navigate('/dashboard');
      return userInfo;
    } catch (err) {
      // Extract the error message using our helper function
      let errorMessage = 'Failed to register';

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (err && typeof err === 'object' && 'message' in err) {
        // Try to extract from complex error object
        errorMessage = extractErrorMessage(err);
      }

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });

      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    clearTenantId(); // Clear tenant ID on logout
    setUser(null);
    setCurrentTenantId(null);
    navigate('/login');

    toast({
      title: 'Success',
      description: 'Logged out successfully',
    });
  };

  return (
    <UserContext.Provider
      value={{
        user,
        isLoading,
        tenantId,
        login,
        logout,
        register,
        fetchUserData,
        switchTenant,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
